import { createContext } from 'react'
import { LS_KEY } from '../constants'

export interface IAuthInfo {
  id_token: string,
  access_token: string,
  email: string,
  refresh_token?: string,
}

export const AuthContext = createContext<Partial<IAuthInfo> & { loading: boolean }>({ loading: false })

export function getAuthInfoFromLocalStorage(): IAuthInfo | null {
  let info: IAuthInfo | null = null;
  try {
    const authInfoString = localStorage.getItem(LS_KEY.AUTH_INFO);
    if (authInfoString) {
      const value = JSON.parse(authInfoString);
      if ("id_token" in value && "access_token" in value && "email" in value) {
        info = value
      } else {
        console.error("Auth info in localStorage is wrong")
      }
    }
  } catch (err) {
    console.error(err);
  }
  return info;
}

/**
 * Whether a membership is served by the portal SPA.
 *
 * `use_portal` comes from /instance/list and /user/membership. It is optional and
 * defaults to false when absent, so a classic per-instance membership stays on its
 * own domain.
 */
function membershipUsesPortal(m: { use_portal?: boolean }): boolean {
  return m.use_portal === true;
}

/**
 * A production build served from localhost (the portal-dev harness) has no classic
 * per-instance frontend to jump to, so on localhost every membership is launched
 * through the portal SPA at this origin instead of its real domain. getEnv() is no
 * help here -- it reports DEV, not LOCAL, for a production build on localhost -- so
 * key off the hostname directly.
 */
function isLocalhost(): boolean {
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]";
}

/**
 * How many of the caller's memberships are BOTH portal-enabled and owned by the
 * caller. Only an owned instance can use the bare /portal shortcut (the SPA
 * resolves it from the token, so nothing names it in the URL); a member has
 * nothing for /portal to resolve. When this count is 1 the sole owned portal
 * instance goes to /portal; every other portal membership is named at
 * /portals/{portal-id}.
 */
export function countOwnerPortalMemberships(
  memberships: { use_portal?: boolean; role?: string }[]
): number {
  return memberships.filter((m) => membershipUsesPortal(m) && m.role === "owner").length;
}

/**
 * Launch a membership's web frontend.
 *
 * use_portal (from /instance/list and /user/membership) decides, per membership,
 * whether it is served by the shared portal SPA at this origin or the per-instance
 * frontend on its own domain. On the portal SPA the URL depends on how many of
 * the caller's OWNED portal memberships (ownerPortalCount, from
 * countOwnerPortalMemberships): when the launched instance is owned AND it is the
 * caller's only owned portal instance, it uses the bare /portal, which the SPA
 * resolves from the token so no Portal Id appears in the URL. Every other portal
 * membership -- a member's instance, or one of several owned ones -- is named at
 * /portals/{portal-id}. The Portal Id is the membership label, the first segment
 * of its domain.
 */
export function launchMembershipPortal(
  instance: {
    id?: string;
    domain: string;
    label?: string;
    name?: string;
    plan?: string;
    plan_id?: string;
    role?: string;
    use_portal?: boolean;
  },
  ownerPortalCount: number,
  target: "_blank" | "_self",
  bid?: string
) {
  if (membershipUsesPortal(instance) || isLocalhost()) {
    // Hand the launched membership to the SPA (same origin) so it resolves the
    // instance at boot without an API round-trip. Only for _self (this tab is
    // navigating into the SPA); a _blank open uses its own boot + API fallback.
    if (target === "_self") storePortalInstance(instance);
    const path =
      instance.role === "owner" && ownerPortalCount <= 1
        ? "/portal"
        : `/portals/${encodeURIComponent(instance.label || instance.domain.split(".")[0])}`;
    return go(bid ? `${path}/activate?bid=${bid}` : path, target);
  }
  const url = instance.domain.startsWith("http") ? instance.domain : `https://${instance.domain}`;
  window.open(bid ? `${url}/activate?bid=${bid}` : url, target);
}

/**
 * Launch a Crystal seat's provisioning page (Part 2 lives inside the MSP
 * instance, at /provisioning/{crystalId} -- a real route in both the portal SPA
 * and the classic per-instance frontend).
 *
 * id is required, not decorative: the portal SPA's boot handoff
 * (readHandoffInstance) discards the blob unless it has a string id + domain,
 * forcing a slower API-resolution fallback instead of using this handoff.
 */
export function launchCrystalProvisioning(
  instance: { id: string; domain: string; label?: string; role?: string; use_portal?: boolean },
  ownerPortalCount: number,
  crystalId: string
) {
  if (membershipUsesPortal(instance) || isLocalhost()) {
    // Hand the launched membership to the SPA (same origin) so it resolves the
    // instance at boot without an API round-trip.
    storePortalInstance(instance);
    const path =
      instance.role === "owner" && ownerPortalCount <= 1
        ? "/portal"
        : `/portals/${encodeURIComponent(instance.label || instance.domain.split(".")[0])}`;
    return go(`${path}/provisioning/${crystalId}`, "_self");
  }
  const url = instance.domain.startsWith("http") ? instance.domain : `https://${instance.domain}`;
  window.location.assign(`${url}/provisioning/${crystalId}`);
}

function go(path: string, target: "_blank" | "_self") {
  if (target === "_self") window.location.assign(path);
  else window.open(path, target);
}

/**
 * Persist the launched membership for the portal SPA to resolve at boot without
 * an API round-trip. The SPA reads it ONCE and deletes it (a one-shot handoff,
 * not a cache), so a later direct/deep-link visit falls back to its API path.
 *
 * Shape mirrors the SPA's IPortalInstanceDTO (snake_case). `owner_email` /
 * `fe_path` are intentionally omitted: the SPA takes the signed-in user's email
 * from the id-token claim, and the frontend version is pinned by the fw-fe*
 * cookie (with the API fallback covering version reconciliation).
 */
function storePortalInstance(instance: {
  id?: string;
  domain: string;
  label?: string;
  name?: string;
  plan?: string;
  plan_id?: string;
  role?: string;
  use_portal?: boolean;
}) {
  try {
    localStorage.setItem(
      LS_KEY.PORTAL_INSTANCE,
      JSON.stringify({
        id: instance.id,
        domain: instance.domain,
        label: instance.label,
        name: instance.name,
        plan: instance.plan,
        plan_id: instance.plan_id,
        role: instance.role,
        use_portal: instance.use_portal,
      })
    );
  } catch {
    /* storage full / disabled: SPA falls back to API resolution */
  }
}

/**
 * Persist a session. The counterpart to clearSession: whatever is written here
 * must be removed there, or a half-cleared session survives a sign-out.
 *
 * AUTH_INFO is the legacy blob the landing page itself reads; the __*__ keys
 * are what the portal shells and the SPA read. Both are written until the
 * classic per-instance frontend is retired.
 */
export function storeSession(info: IAuthInfo) {
  localStorage.setItem(LS_KEY.AUTH_INFO, JSON.stringify(info));
  localStorage.setItem(LS_KEY.ID_TOKEN, info.id_token);
  localStorage.setItem(LS_KEY.ACCESS_TOKEN, info.access_token);
  if (info.refresh_token) {
    localStorage.setItem(LS_KEY.REFRESH_TOKEN, info.refresh_token);
  }
  // How to renew them, recorded by whoever issued them.
  localStorage.setItem(LS_KEY.AUTH_CLIENT, process.env.REACT_APP_CLIENT_ID || "");
  localStorage.setItem(LS_KEY.AUTH_REGION, process.env.REACT_APP_COGNITO_REGION || "");
}

/**
 * Remove every trace of the session from this origin.
 *
 * A session is not just AUTH_INFO any more: the portal keys are what the shared
 * SPA reads, and the fw-fe* cookies pin which frontend version it is served.
 * Clearing only AUTH_INFO leaves the portal signed in, which is why every
 * sign-out path must go through here rather than removing keys itself.
 */
export function clearSession() {
  [LS_KEY.AUTH_INFO, LS_KEY.ID_TOKEN, LS_KEY.ACCESS_TOKEN, LS_KEY.REFRESH_TOKEN,
   LS_KEY.AUTH_CLIENT, LS_KEY.AUTH_REGION, LS_KEY.PORTAL_INSTANCE]
    .forEach((k) => localStorage.removeItem(k));
  // Version cookies are path-scoped per mount, so each has to be expired on the
  // path it was set on -- Path=/ would not match them.
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    // "fw-fe" is the own-instance cookie; "fw-fe-{portal-id}" a named one. Test
    // for both -- a startsWith("fw-fe-") check silently skips the own-instance one.
    if (name !== "fw-fe" && !name.startsWith("fw-fe-")) return;
    const portalId = name === "fw-fe" ? "" : name.slice("fw-fe-".length);
    const paths = portalId ? ["/portal", `/portals/${portalId}`, "/"] : ["/portal", "/"];
    paths.forEach((path) => {
      document.cookie = `${name}=; Path=${path}; Max-Age=0; SameSite=Lax`;
    });
  });
}

export function getLoginServerFromLocalStorage(): string {
  let loginServer = '';
  try {
    loginServer = localStorage.getItem(LS_KEY.LOGIN_SERVER) || process.env.REACT_APP_LOGIN_SERVER || '';
  } catch (err) {
    console.error(err);
  }
  return loginServer;
}